import React, { useCallback, useState } from 'react'

import { Button, Drawer, Well } from '@pushly/aqe/lib/components'
import { DurationInput, DurationMetric } from '@pushly/aqe/lib/components/duration-input'
import { EditOutlined } from '@ant-design/icons'
import { useService } from '@pushly/aqe/lib/hooks/use-service'
import { AccountService } from '../../services'
import { UpdateAccountRequest } from '@pushly/models/lib/structs/accounts'
import { getAccountResponseFromAccountDto } from '../../models/account-helpers'
import { useOrgContextWithReducer } from '../../contexts/org-context'
import { Alert, ConfigProvider, Form, Typography } from 'antd'
import { SETTINGS_WELL_FORM_LAYOUT } from '../../theme/constants'
import { DEFAULT_DRAWER_FORM_ITEM_LAYOUT } from '../../constants'
import { IdleTimeoutLabel, AbsoluteTimeoutLabel } from './labels'

function formatTimeoutDisplay(minutes: number, metric: DurationMetric): string {
    const value = metric === 'hours' ? minutes / 60 : minutes
    const label = metric === 'hours' ? 'hour(s)' : 'minute(s)'
    return `${value} ${label}`
}

function bestMetricForMinutes(minutes: number): DurationMetric {
    return minutes >= 60 && minutes % 60 === 0 ? 'hours' : 'minutes'
}

function convertToMinutes(value: number, metric: DurationMetric): number {
    return metric === 'hours' ? value * 60 : value
}

function convertFromMinutes(minutes: number, metric: DurationMetric): number {
    return metric === 'hours' ? minutes / 60 : minutes
}

export function validateIdleTimeoutMinutes(minutes: number | null | undefined): string | undefined {
    if (minutes === null || minutes === undefined) {
        return 'A value is required'
    }
    if (!Number.isInteger(minutes)) {
        return 'Value must be a whole number'
    }
    if (minutes < 15) {
        return 'Minimum is 15 minutes'
    }
    if (minutes > 1440) {
        return 'Maximum is 1440 minutes (24 hours)'
    }
    return undefined
}

export function validateAbsoluteTimeoutMinutes(minutes: number | null | undefined): string | undefined {
    if (minutes === null || minutes === undefined) {
        return 'A value is required'
    }
    if (!Number.isInteger(minutes)) {
        return 'Value must be a whole number'
    }
    if (minutes < 60) {
        return 'Minimum is 60 minutes (1 hour)'
    }
    if (minutes > 1440) {
        return 'Maximum is 1440 minutes (24 hours)'
    }
    return undefined
}

export function validateCrossField(
    idleMinutes: number | null | undefined,
    absoluteMinutes: number | null | undefined,
): string | undefined {
    if (
        idleMinutes != null &&
        absoluteMinutes != null &&
        Number.isInteger(idleMinutes) &&
        Number.isInteger(absoluteMinutes)
    ) {
        if (idleMinutes > absoluteMinutes) {
            return 'Idle timeout cannot exceed absolute timeout'
        }
    }
    return undefined
}

interface EditorState {
    idleValue: number | undefined
    idleMetric: DurationMetric
    absoluteValue: number | undefined
    absoluteMetric: DurationMetric
}

export const SessionTimeoutSettings: React.FC = () => {
    const orgSvc = useService(AccountService)
    const [{ org }, dispatch] = useOrgContextWithReducer()
    const { id: orgId } = org

    const [editorVisible, setEditorVisible] = useState(false)
    const [editor, setEditor] = useState<EditorState>(() => {
        const idleMetric = bestMetricForMinutes(org.idle_timeout_minutes)
        const absoluteMetric = bestMetricForMinutes(org.absolute_timeout_minutes)
        return {
            idleValue: convertFromMinutes(org.idle_timeout_minutes, idleMetric),
            idleMetric,
            absoluteValue: convertFromMinutes(org.absolute_timeout_minutes, absoluteMetric),
            absoluteMetric,
        }
    })

    const idleDisplayMetric = bestMetricForMinutes(org.idle_timeout_minutes)
    const absoluteDisplayMetric = bestMetricForMinutes(org.absolute_timeout_minutes)

    const openEditor = useCallback(() => {
        const idleMetric = bestMetricForMinutes(org.idle_timeout_minutes)
        const absoluteMetric = bestMetricForMinutes(org.absolute_timeout_minutes)
        setEditor({
            idleValue: convertFromMinutes(org.idle_timeout_minutes, idleMetric),
            idleMetric,
            absoluteValue: convertFromMinutes(org.absolute_timeout_minutes, absoluteMetric),
            absoluteMetric,
        })
        setEditorVisible(true)
    }, [org.idle_timeout_minutes, org.absolute_timeout_minutes])

    const closeEditor = useCallback(() => {
        setEditorVisible(false)
    }, [])

    const resolveMinutes = (value: number | undefined, metric: DurationMetric): number | null => {
        if (value === undefined) return null
        return convertToMinutes(value, metric)
    }

    const idleMinutes = resolveMinutes(editor.idleValue, editor.idleMetric)
    const absoluteMinutes = resolveMinutes(editor.absoluteValue, editor.absoluteMetric)

    const idleError = validateIdleTimeoutMinutes(idleMinutes) || validateCrossField(idleMinutes, absoluteMinutes)
    const absoluteError = validateAbsoluteTimeoutMinutes(absoluteMinutes)
    const hasErrors = !!idleError || !!absoluteError

    const hasChanges =
        idleMinutes !== org.idle_timeout_minutes || absoluteMinutes !== org.absolute_timeout_minutes

    const updateSettings = useCallback(async () => {
        if (hasErrors || idleMinutes == null || absoluteMinutes == null) return

        const req = new UpdateAccountRequest()
        req.idle_timeout_minutes = idleMinutes
        req.absolute_timeout_minutes = absoluteMinutes

        const res = await orgSvc.patch(orgId, req, true)
        if (res.ok) {
            const accountResponse = getAccountResponseFromAccountDto(res.data)
            dispatch({
                type: 'patch_session_timeouts',
                value: {
                    idle_timeout_minutes: accountResponse.idle_timeout_minutes,
                    absolute_timeout_minutes: accountResponse.absolute_timeout_minutes,
                },
            })
            setEditorVisible(false)
        }
    }, [hasErrors, idleMinutes, absoluteMinutes, orgId, orgSvc, dispatch])

    return (
        <Well
            title="Session Configuration"
            className="session-timeout-settings type-3"
            hideFooter={true}
            actions={
                <Button size="small" onClick={openEditor}>
                    <EditOutlined />
                    <span>Edit</span>
                </Button>
            }
        >
            <Form {...SETTINGS_WELL_FORM_LAYOUT} labelAlign="left" colon={false}>
                <Form.Item label={<IdleTimeoutLabel />}>
                    <Typography.Text>
                        {formatTimeoutDisplay(org.idle_timeout_minutes, idleDisplayMetric)}
                    </Typography.Text>
                </Form.Item>
                <Form.Item label={<AbsoluteTimeoutLabel />}>
                    <Typography.Text>
                        {formatTimeoutDisplay(org.absolute_timeout_minutes, absoluteDisplayMetric)}
                    </Typography.Text>
                </Form.Item>
            </Form>

            <Drawer
                className="session-timeout-settings-editor"
                title="Edit Session Configuration"
                open={editorVisible}
                onClose={closeEditor}
                destroyOnHidden={true}
                onSubmit={() => updateSettings()}
                disableSubmit={!hasChanges || hasErrors}
                width={800}
            >
                <ConfigProvider theme={{ components: { Form: { itemMarginBottom: 16 } } }}>
                <Alert
                    type="info"
                    showIcon
                    message="Changes apply on each user's next request and do not immediately terminate active sessions."
                    style={{ marginBottom: 24 }}
                />
                <Form {...DEFAULT_DRAWER_FORM_ITEM_LAYOUT} labelAlign="left" colon={false} requiredMark={false}>
                    <Form.Item
                        label={<IdleTimeoutLabel />}
                        validateStatus={idleError ? 'error' : undefined}
                        help={idleError}
                    >
                        <DurationInput
                            value={editor.idleValue}
                            metric={editor.idleMetric}
                            onValueChange={(v) => setEditor((prev) => ({ ...prev, idleValue: v }))}
                            onMetricChange={(m) => setEditor((prev) => ({ ...prev, idleMetric: m }))}
                            min={15}
                            max={1440}
                        />
                    </Form.Item>

                    <Form.Item
                        label={<AbsoluteTimeoutLabel />}
                        validateStatus={absoluteError ? 'error' : undefined}
                        help={absoluteError}
                    >
                        <DurationInput
                            value={editor.absoluteValue}
                            metric={editor.absoluteMetric}
                            onValueChange={(v) => setEditor((prev) => ({ ...prev, absoluteValue: v }))}
                            onMetricChange={(m) => setEditor((prev) => ({ ...prev, absoluteMetric: m }))}
                            min={60}
                            max={1440}
                        />
                    </Form.Item>
                </Form>
                </ConfigProvider>
            </Drawer>
        </Well>
    )
}
